import { Button, Form, Input, Modal, Typography } from 'antd';
import React, { useState } from 'react';
import { useEnterKeyListener } from '../../shared/useEnterKeyListener';
import { SecretBuilderState } from './types';
import { useTranslation } from '../../../i18n/useTranslation';

const NAME_FIELD_NAME = 'name';
const DESCRIPTION_FIELD_NAME = 'description';
const VALUE_FIELD_NAME = 'value';

type Props = {
    initialState?: SecretBuilderState;
    visible: boolean;
    onSubmit?: (source: SecretBuilderState, resetState: () => void) => void;
    onCancel?: () => void;
};

export const SecretBuilderModal = ({ initialState, visible, onSubmit, onCancel }: Props) => {
    const { t } = useTranslation();
    const [createButtonEnabled, setCreateButtonEnabled] = useState(false);
    const [form] = Form.useForm();

    // Handle the Enter press
    useEnterKeyListener({
        querySelectorToExecuteClick: '#createSecretButton',
    });

    function resetValues() {
        form.resetFields();
    }

    const handleCancel = () => {
        resetValues();
        setCreateButtonEnabled(false);
        onCancel?.();
    };

    return (
        <Modal
            centered
            zIndex={1009} // one higher than other modals - needed for managed ingestion forms
            visible={visible}
            className="f-modal"
            onCancel={handleCancel}
            title={<h1 className="f-modal-title">{t('Create a new Secret')}</h1>}
            footer={
                <>
                    <Button type="default" onClick={handleCancel}>
                        {t('Cancel')}
                    </Button>
                    <Button
                        type="primary"
                        id="createSecretButton"
                        disabled={!createButtonEnabled}
                        data-testid="secret-modal-create-button"
                        onClick={() =>
                            onSubmit?.(
                                {
                                    name: form.getFieldValue(NAME_FIELD_NAME),
                                    description: form.getFieldValue(DESCRIPTION_FIELD_NAME),
                                    value: form.getFieldValue(VALUE_FIELD_NAME),
                                },
                                resetValues,
                            )
                        }
                    >
                        {t('Create')}
                    </Button>
                </>
            }
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={initialState}
                onFieldsChange={() => {
                    const hasErrors = form.getFieldsError().some((field) => field.errors.length > 0);
                    const nameValue = form.getFieldValue(NAME_FIELD_NAME);
                    const valueValue = form.getFieldValue(VALUE_FIELD_NAME);
                    const hasRequiredValues = nameValue && nameValue.trim() && valueValue && valueValue.trim();

                    setCreateButtonEnabled(!hasErrors && hasRequiredValues);
                }}
            >
                <Form.Item label={<Typography.Text>{t('Name')}</Typography.Text>}>
                    <Form.Item
                        name={NAME_FIELD_NAME}
                        data-testid="secret-modal-name-input"
                        className="f-form-item-without-margin"
                        rules={[
                            {
                                required: true,
                                message: t('Enter Name'),
                            },
                            { whitespace: false },
                            { min: 1, max: 50 },
                            {
                                pattern: /^[A-Za-z_]+$/,
                                message: t('ONLY_LETTERS_UNDERSCORES_SECRET_NAME'),
                            },
                        ]}
                        hasFeedback
                    >
                        <Input placeholder={t('A name for your secret')} />
                    </Form.Item>
                    <Typography.Paragraph>{t('GIVE_SECRET_NAME_DESC')}</Typography.Paragraph>
                </Form.Item>
                <Form.Item label={<Typography.Text>{t('Value')}</Typography.Text>}>
                    <Form.Item
                        hasFeedback
                        name={VALUE_FIELD_NAME}
                        data-testid="secret-modal-value-input"
                        className="f-form-item-without-margin"
                        rules={[
                            {
                                required: true,
                                message: t('Enter Value'),
                            },
                            // { whitespace: true },
                            { min: 1 },
                        ]}
                    >
                        <Input.TextArea autoComplete="false" placeholder={t('The value of your secret')} />
                    </Form.Item>
                    <Typography.Paragraph>{t('SECRET_VALUE_DESCRIPTION')}</Typography.Paragraph>
                </Form.Item>
                <Form.Item label={<Typography.Text>{t('Description')}</Typography.Text>}>
                    <Form.Item
                        hasFeedback
                        name={DESCRIPTION_FIELD_NAME}
                        className="f-form-item-without-margin"
                        data-testid="secret-modal-description-input"
                        rules={[{ whitespace: true }, { min: 1, max: 500 }]}
                    >
                        <Input.TextArea style={{ minHeight: 72 }} placeholder={t('A description for your secret')} />
                    </Form.Item>
                    <Typography.Paragraph>
                        {t('An optional description to help keep track of your secret.')}
                    </Typography.Paragraph>
                </Form.Item>
            </Form>
        </Modal>
    );
};
